import React, { useContext, useMemo, useState } from "react";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import { Prompt } from "react-router";
import { Link } from "react-router-dom";
import { Button, ButtonGroup, Spinner } from "@blueprintjs/core";

import { AppToaster, MenuMultiSelect } from "@components";
import {
  useActions,
  useBasePath,
  useRepos,
  useUpdateRepoMutation,
} from "@hooks";
import { OrgContext } from "@providers";
import { RepoChanges } from "@types";

import { ItemHeading, SettingSwitch } from "@pages/projects/components";
import { Repo } from "@pages/projects/types";
import { Action } from "@pages/settings/types";

import { PolicyWithRules } from "../../types";
import { onUpdatePolicy } from "../../utils";

import { ProjectsSummaryPanel } from "./ProjectsSummaryPanel";

type Props = {
  workingPolicy: PolicyWithRules;
  setWorkingPolicy: (p: PolicyWithRules | undefined) => void;
  refreshPolicies: () => void;
};

export const PolicySettings: React.FC<Props> = ({
  workingPolicy,
  setWorkingPolicy,
  refreshPolicies,
}) => {
  const { org } = useContext(OrgContext);
  const { data: orgRepos } = useRepos();
  const { data: actions } = useActions();
  const basePath = useBasePath();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // 'stashed' saves a copy of policy before any unsaved changes were made
  const [stashed, setStashed] = useState<PolicyWithRules | undefined>();
  const [projectsWithoutPolicy, setProjectsWithoutPolicy] = useState<Repo[]>(
    []
  );

  const isIntegrationSelected = (item: Action) => {
    return workingPolicy!.notifications!.some((i) => i.name === item.name);
  };

  const policyHasUnsavedChanges = () => {
    return stashed !== undefined && !isEqual(stashed, workingPolicy);
  };

  const updatePolicyPRComments = useMemo(
    () => () => {
      if (!stashed) setStashed(workingPolicy);
      const newPolicy = {
        ...workingPolicy!,
        pr_comments: !workingPolicy!.pr_comments,
      };
      setWorkingPolicy(newPolicy);
    },
    [workingPolicy, setWorkingPolicy, stashed, setStashed]
  );

  const updatePolicyBlocking = useMemo(
    () => () => {
      if (!stashed) setStashed(workingPolicy);
      const newPolicy = {
        ...workingPolicy!,
        blocking: !workingPolicy!.blocking,
      };
      setWorkingPolicy(newPolicy);
    },
    [workingPolicy, setWorkingPolicy, stashed, setStashed]
  );

  const updatePolicyIntegrations = useMemo(
    () => (value: Action) => {
      // save policy in 'stashed' for state knowledge of whether unsaved changes exist
      if (!stashed) setStashed(workingPolicy);

      // toggles a particular item on or off in the popup menu
      if (workingPolicy!.notifications?.find((s) => s.name === value.name)) {
        setWorkingPolicy({
          ...workingPolicy!,
          notifications: workingPolicy!.notifications.filter(
            (s) => s.name !== value.name
          ),
        });
      } else {
        setWorkingPolicy({
          ...workingPolicy!,
          notifications: sortBy(
            [...workingPolicy!.notifications!, value],
            (s) => s.name
          ),
        });
      }
    },
    [workingPolicy, setWorkingPolicy, stashed, setStashed]
  );

  /*** Add this policy to projects section  ***/

  useMemo(() => {
    const workingPolicyRepoIds =
      workingPolicy !== undefined
        ? workingPolicy?.repos.map((elem) => elem.id)
        : [];
    setProjectsWithoutPolicy(
      orgRepos === undefined
        ? []
        : orgRepos.filter((elem) => !workingPolicyRepoIds.includes(elem.id!))
    );
  }, [orgRepos, workingPolicy]);

  const addProject = (repo: Repo) => {
    const change: RepoChanges = {
      policies: [...repo.policies, workingPolicy!],
    };
    submitChange(repo, change);
  };

  const removeProject = (repo: Repo) => {
    const change: RepoChanges = {
      policies: repo.policies.filter((pol) => pol.slug !== workingPolicy!.slug),
    };
    submitChange(repo, change, true);
  };

  const { mutateAsync } = useUpdateRepoMutation();

  const submitChange = (
    repo: Repo,
    change: RepoChanges,
    remove: boolean = false
  ) => {
    mutateAsync({ deploymentId: org!.id, repo: repo, repoChanges: change })
      .then(() => {
        AppToaster.show({
          message: `Successfully ${
            remove ? "removed policy from" : "added policy to"
          } ${repo.name}`,
          intent: "success",
        });
        refreshPolicies();
      })
      .catch((err) =>
        AppToaster.show({
          message: `Failed to ${
            remove ? "remove policy from" : "add policy to"
          } ${repo.name} - ${err}`,
          intent: "danger",
        })
      );
  };

  /*** End: Add this policy to projects section  ***/

  /** Called when the user clicks the "discard changes" button
   *
   * Policy state is reverted to the last saved value.
   */
  const onDiscardChanges = useMemo(
    () => () => {
      setWorkingPolicy(stashed);
      setStashed(undefined);
    },
    [setWorkingPolicy, stashed, setStashed]
  );

  const isGitlabAuthOrg = org?.source_type === "gitlab";
  const commentsType = isGitlabAuthOrg ? "MR" : "PR";
  const commentsTarget = isGitlabAuthOrg
    ? "GitLab merge requests"
    : "GitHub pull requests";

  return (
    <>
      <Prompt
        when={policyHasUnsavedChanges()}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      {workingPolicy === undefined ? (
        <h3>Policy not found</h3>
      ) : (
        <>
          {/* TODO: incorporate this into the notification channels multiselect */}
          <SettingSwitch
            title={`Inline ${commentsType} Comments`}
            description={`Have Semgrep comment directly on findings in your ${commentsTarget}.`}
            value={workingPolicy.pr_comments}
            onChange={updatePolicyPRComments}
          />
          <SettingSwitch
            title={"Blocking"}
            description={
              "Semgrep should fail the build if this policy has findings."
            }
            value={workingPolicy.blocking}
            onChange={updatePolicyBlocking}
          />
          <div>
            <ItemHeading>Projects</ItemHeading>
          </div>
          <div>
            <ProjectsSummaryPanel
              repos={
                orgRepos === undefined
                  ? []
                  : orgRepos.filter((elem) =>
                      workingPolicy.repos.map((r) => r.id).includes(elem.id!)
                    )
              }
              projectsWithoutPolicy={projectsWithoutPolicy}
              onRemove={removeProject}
              onAdd={addProject}
            />
          </div>
          <div>
            <ItemHeading>Integrations</ItemHeading>
          </div>
          {actions && actions.length ? (
            <div>
              <MenuMultiSelect<Action>
                placeholder="Add..."
                items={actions}
                isItemSelected={(item) => isIntegrationSelected(item)}
                extractor={(p) => p.name} // fxn to convert action to what is displayed for each action
                onItemSelect={updatePolicyIntegrations}
                onRemove={updatePolicyIntegrations}
                selectedItems={workingPolicy!.notifications} // items in text box
                popoverProps={{
                  minimal: true,
                }}
              />
            </div>
          ) : (
            <div style={{ backgroundColor: "orange" }}>
              No integrations found! &nbsp;
              <Link to={`${basePath}/integrations`}>Click here</Link>
              &nbsp; to complete setup
            </div>
          )}
        </>
      )}

      {isSaving ? (
        <Spinner intent="primary" />
      ) : stashed && workingPolicy && policyHasUnsavedChanges() ? (
        <ButtonGroup style={{ marginTop: "var(--global-spacing-xl)" }}>
          <Button
            onClick={onUpdatePolicy(
              org!,
              workingPolicy,
              setIsSaving,
              setStashed,
              refreshPolicies
            )}
            intent="primary"
          >
            Save changes
          </Button>
          <Button onClick={onDiscardChanges} intent="none">
            Discard changes
          </Button>
        </ButtonGroup>
      ) : undefined}
    </>
  );
};
